import { useContext, useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box, Button, Container, Divider, Dropdown, IconButton,
  Menu, MenuButton, MenuItem, Sheet, Stack, Typography, Drawer,
  List, ListItem, ListItemButton, Badge, Tooltip
} from "@mui/joy";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import BallotRoundedIcon from '@mui/icons-material/BallotRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';

import { LayoutContext } from "../../context";
import { getFirstName, getDisplayName } from "../../shared/nameUtils";
import { useLocation, useNavigate } from "react-router";
import { StoriesRow, StoriesInline } from "./StoriesBar";


// ── Цвета Planium ────────────────────────────────────────────
const C = {
  dark: '#1a1a2e',
  darkLight: '#16213e',
  primary: '#6366f1',
  primaryDark: '#4f46e5',
  primaryLight: '#818cf8',
  accent: '#8b5cf6',
  gray: '#64748b',
  grayLight: '#94a3b8',
  white: '#ffffff',
  border: 'rgba(255,255,255,0.08)',
};

// ── Навигация для главной страницы ──────────────────────────
const mainNavItems = [
  { label: 'Главная', href: '/', icon: <HomeRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} /> },
  { label: 'Калькулятор', href: '/#calculator', icon: <CalculateRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} /> },
  { label: 'О нас', href: '/#for-whom', icon: <InfoRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} /> },
  { label: 'FAQ', href: '/#faq', icon: <HelpOutlineRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} /> },
];

function NavPill({ label, href, active, onClick }) {
  return (
    <Box
      component={href ? 'a' : 'button'}
      href={href}
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: '6px',
        px: 2, py: '7px', borderRadius: '100px',
        fontSize: '13.5px', fontWeight: active ? 700 : 500,
        cursor: 'pointer', textDecoration: 'none',
        border: 'none', fontFamily: 'inherit', whiteSpace: 'nowrap',
        transition: 'all 0.2s',
        background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
        color: active ? C.white : C.grayLight,
        '&:hover': { color: C.white, background: 'rgba(99,102,241,0.1)' },
      }}
    >
      {label}
    </Box>
  );
}

// Контент может скроллиться во внутреннем контейнере — ищем ближайшего скроллящегося родителя.
function getScrollParent(node) {
  let el = node?.parentElement;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return window;
}
const STORIES_HEADER_H = 62; // высота основной строки шапки клиента

export default function Header({ onShowOrder }) {
  const {
    account, subscribeUserToPush, checkNotificationStatus, getPlatform, logout, setModalAuth,
    storiesBar, loadStoriesBar, newsStories, setNewsStories,
  } = useContext(LayoutContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Сторис в шапке ──────────────────────────────────────────
  const storyItems = useMemo(() => storiesBar?.items || [], [storiesBar]);
  const storiesModalOpen = !!newsStories?.open;
  const [storyRowVisible, setStoryRowVisible] = useState(true);
  const storyRowRef = useRef(null);
  const storyScrollElRef = useRef(null);

  const isLK = location.pathname.includes("/lk");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const canSeeAccessTab =
    (account?.is_add_people && !account?.is_child_account) ||
    (account?.is_child_account && (
      account?.my_permissions === '__all__' ||
      account?.my_permissions?.includes('add_access') ||
      account?.my_permissions?.includes('edit_access') ||
      account?.my_permissions?.includes('delete_access')
    ));

  const canSeePaymentsTab =
    account?.my_permissions === '__all__' ||
    (Array.isArray(account?.my_permissions) && (
      account.my_permissions.includes('view_payments') ||
      account.my_permissions.includes('manage_payments')
    ));

  // Грузим кольца при входе в ЛК и при каждом закрытии плеера (обновить «просмотрено»).
  useEffect(() => {
    if (!isLK || !account?.id) return;
    if (!storiesModalOpen) loadStoriesBar?.();
  }, [isLK, account?.id, storiesModalOpen, loadStoriesBar]);

  // Лента уехала под шапку → показываем компактную стопку в строке.
  useEffect(() => {
    if (!storyItems.length || !storyRowRef.current) { setStoryRowVisible(true); return; }
    const scroller = getScrollParent(storyRowRef.current);
    storyScrollElRef.current = scroller;
    const root = scroller === window ? null : scroller;
    const io = new IntersectionObserver(
      ([entry]) => setStoryRowVisible(entry.isIntersecting),
      { root, rootMargin: `-${STORIES_HEADER_H + 4}px 0px 0px 0px`, threshold: 0 },
    );
    io.observe(storyRowRef.current);
    return () => io.disconnect();
  }, [storyItems.length]);

  const openStoryAt = (idx) => setNewsStories({ open: true, items: storyItems, startIndex: idx });
  const scrollStoriesTop = () => {
    const s = storyScrollElRef.current;
    if (s === window) window.scrollTo({ top: 0, behavior: 'smooth' });
    else if (s) s.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const hasStories = storyItems.length > 0;
  const showStoriesInline = hasStories && !storyRowVisible;

  // Добавить обработчик:
  const handleEnableNotifications = async () => {
    if (!account) return;

    const status = checkNotificationStatus();
    if (!status?.supported) {
        alert('Ваш браузер не поддерживает уведомления');
        return;
    }

    if (status.permission === 'granted') {
        alert('Push-уведомления уже включены!');
        return;
    }

    if (status.permission === 'denied') {
        const platform = getPlatform();
        if (platform === 'ios') {
            alert(
                'Уведомления отключены.\n\n' +
                '1. Откройте Настройки → Safari → Уведомления\n' +
                '2. Найдите этот сайт и включите уведомления'
            );
        } else {
            alert(
                'Вы отключили уведомления.\n\n' +
                '1. Нажмите 🔒 слева от адресной строки\n' +
                '2. Выберите «Настройки сайта»\n' +
                '3. Найдите «Уведомления» → «Разрешить»'
            );
        }
        return;
    }

    const result = await subscribeUserToPush();
    switch (result?.type) {
        case 'success':
            alert('Push-уведомления подключены!');
            break;
        case 'permission_denied':
            alert('Вы отклонили запрос на уведомления');
            break;
        case 'ios_need_install':
            alert(
                'Для уведомлений на iPhone:\n\n' +
                '1. Нажмите «Поделиться» (внизу Safari)\n' +
                '2. Выберите «На экран Домой»\n' +
                '3. Откройте приложение с домашнего экрана'
            );
            break;
        case 'not_supported':
            alert('Ваш браузер не поддерживает push-уведомления');
            break;
    }
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'sticky', top: 0, left: 0, right: 0,
          zIndex: 1000, transition: 'all 0.3s ease',
        }}
      >
        {/* ── TOP BAR (только на десктопе, только на главной) ───── */}
        {!isLK && (
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              background: C.darkLight,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Box sx={{ maxWidth: isLK ? 1200 : 1280, mx: 'auto', px: 3, py: '6px' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing="5px">
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
                    <Typography sx={{ fontSize: 12, color: C.grayLight }}>
                      Пн-Вс 9:00–21:00
                    </Typography>
                  </Stack>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: C.gray }} />
                  <Typography sx={{ fontSize: 12, color: C.grayLight }}>
                    Замеры по всей России
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    component="a"
                    href="tel:+79011326059"
                    sx={{
                      fontSize: 12, color: C.grayLight, textDecoration: 'none',
                      transition: 'color 0.2s', '&:hover': { color: C.white },
                      display: 'flex', alignItems: 'center', gap: 0.5,
                    }}
                  >
                    <PhoneRoundedIcon sx={{ fontSize: 13 }} />
                    +7 (901) 132-60-59
                  </Box>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: C.gray }} />
                  <Box
                    component="a"
                    href="https://max.ru/id590852001703_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: 12, color: C.grayLight, textDecoration: 'none',
                      transition: 'color 0.2s', '&:hover': { color: C.white },
                      display: 'flex', alignItems: 'center', gap: 0.5,
                    }}
                  >
                    <SmartToyRoundedIcon sx={{ fontSize: 13 }} />
                    Бот в Макс
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}

        {/* ── MAIN BAR ─────────────────────────────────────────── */}
        <Box
          sx={{
            background: scrolled ? 'rgba(26,26,46,0.92)' : C.dark,
            backdropFilter: scrolled ? 'blur(20px)' : 'none',
            borderBottom: scrolled
              ? '1px solid rgba(255,255,255,0.06)'
              : '1px solid transparent',
            transition: 'all 0.3s ease',
          }}
        >
          <Box sx={{ maxWidth: isLK ? 1200 : 1280, mx: 'auto', px: { xs: 0.5, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ height: 62, gap: { xs: 0, sm: 2, md: 3 } }}
            >
              {/* ── Logo ── */}
              <Stack direction="row" alignItems="center" sx={{ gap: { xs: 0, sm: 1.5 }, flexShrink: 1, minWidth: 0 }}>
                <motion.div
                  whileHover={{ rotate: [-5, 5, -3, 0] }}
                  transition={{ duration: 0.4 }}
                  style={{ display: 'flex' }}
                >
                  <Box
                    component="img"
                    src="/logo.png"
                    sx={{ width: 38, height: 38, display: { xs: 'none', sm: 'block' } }}
                  />
                </motion.div>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 15, fontWeight: 800,
                      color: C.white, letterSpacing: '-0.3px',
                    }}
                  >
                    PLANIUM
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: C.grayLight, mt: -0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Ваш персональный замерщик!
                  </Typography>
                </Box>
              </Stack>

              {/* ── Nav island (десктоп, только на главной) ── */}
              {!isLK && (
                <Stack
                  component="nav"
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '100px',
                    p: '4px',
                  }}
                >
                  {mainNavItems.map((item) => (
                    <NavPill
                      key={item.href}
                      href={item.href}
                      label={item.label}
                      active={location.pathname === item.href || location.hash === item.href.replace('/', '')}
                    />
                  ))}
                </Stack>
              )}

              {/* ── Right side ── */}
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, gap: { xs: 0.5, sm: 1.5 } }}>
                
                {/* Свёрнутые сторис — появляются, когда лента уехала под шапку */}
                <AnimatePresence initial={false}>
                  {showStoriesInline && (
                    <StoriesInline items={storyItems} onOpen={openStoryAt} onExpand={scrollStoriesTop} />
                  )}
                </AnimatePresence>

                {/* Телефон — десктоп */}
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                  <Box component="a" href="tel:+79011326059" sx={{ textDecoration: 'none' }}>
                    <Typography
                      sx={{
                        fontSize: 15, fontWeight: 800, color: C.white,
                        fontFamily: 'inherit', lineHeight: 1.2,
                      }}
                    >
                      +7 (901) 132-60-59
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10, color: C.gray,
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                      }}
                    >
                      Номер для связи
                    </Typography>
                  </Box>
                </Box>

                {/* CTA кнопка — только на главной, десктоп */}
                {!isLK && (
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{ display: 'flex' }}
                  >
                    <Box
                      component="a"
                      href="https://max.ru/id590852001703_bot"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center', gap: 1,
                        background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
                        color: C.white,
                        px: 2.5, py: '10px', borderRadius: '100px',
                        fontSize: '13.5px', fontWeight: 700, textDecoration: 'none',
                        transition: 'all 0.2s', whiteSpace: 'nowrap',
                        boxShadow: '0 3px 12px rgba(99,102,241,0.3)',
                        '&:hover': { transform: 'translateY(-1px)' },
                      }}
                    >
                      <SmartToyRoundedIcon sx={{ fontSize: 16, color: C.white }} />
                      МАКС
                    </Box>
                  </motion.div>
                )}

                {/* ── Кнопка профиля / Войти ── */}
                {account ? (
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{
                        root: {
                          variant: "plain", size: "md",
                          sx: {
                            borderRadius: 12,
                            fontWeight: 700,
                            fontSize: 14,
                            px: { xs: 1, sm: 2 },
                            maxWidth: { xs: 150, sm: 'none' },
                            whiteSpace: 'nowrap', overflow: 'hidden',
                            gap: 0.5,
                            color: C.white,
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            '&:hover': { background: 'rgba(255,255,255,0.14)', color: C.white },
                          },
                        }
                      }}
                    >
                      <PersonRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} />
                      {getFirstName(account) || "К"}
                    </MenuButton>

                    <Menu
                      placement="bottom-end"
                      sx={{
                        minWidth: 220, borderRadius: "16px", p: 0.5,
                        boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                        background: C.darkLight,
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography level="title-sm" fontWeight={700} sx={{ color: C.white }}>
                          {getDisplayName(account) || "Клиент"}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: C.grayLight }}>
                          {account.phone}
                        </Typography>

                        {/* Кнопка Push-уведомлений */}
                        {account && checkNotificationStatus()?.permission !== 'granted' && (
                            <Tooltip title="Включить уведомления">
                                <Button
                                    variant="soft"
                                    color="danger"
                                    size="sm"
                                    onClick={handleEnableNotifications}
                                    startDecorator={<NotificationsIcon />}
                                    sx={{ mt: 1 }}
                                >
                                    Вкл. уведомления
                                </Button>
                            </Tooltip>
                        )}

                        {account && checkNotificationStatus()?.permission === 'granted' && (
                          <Sheet color="neutral" variant="soft" sx={{ backgroundColor: "#5656a9", px: 1, mt: 1, py: 0.5, borderRadius: 'xl', width: 'fit-content' }}>
                            <Typography level="body-sm" sx={{ color: 'neutral.100' }} startDecorator={<NotificationsIcon color="success" sx={{ color: 'success.400', fontSize: 16 }} />}>
                              Уведом. подкл.
                            </Typography>
                          </Sheet>
                        )}
                      </Box>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                      {isLK && (
                        <MenuItem
                          onClick={() => navigate("/", { replace: true })}
                          sx={{
                            borderRadius: "10px", mt: 0.5, color: C.grayLight,
                            '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                          }}
                        >
                          <StarRateRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                          О нас
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => navigate("/lk/profile", { replace: true })}
                        sx={{
                          borderRadius: "10px", mt: 0.5, color: C.grayLight,
                          '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                        }}
                      >
                        <AccountCircleRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                        Профиль
                      </MenuItem>
                      <MenuItem
                        onClick={() => navigate("/lk/notifications", { replace: true })}
                        sx={{
                          borderRadius: "10px", mt: 0.5, color: C.grayLight,
                          '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                        }}
                      >
                        <NotificationsIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                        Уведомления
                      </MenuItem>

                      <MenuItem
                        onClick={() => navigate("/lk/documents", { replace: true })}
                        sx={{
                          borderRadius: "10px", mt: 0.5, color: C.grayLight,
                          '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                        }}
                      >
                        <DescriptionRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                        Документы
                      </MenuItem>

                      <MenuItem
                        onClick={() => navigate("/lk/referral", { replace: true })}
                        sx={{
                          borderRadius: "10px", mt: 0.5, color: C.grayLight,
                          '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                        }}
                      >
                        <CardGiftcardRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                        Реферальная программа
                      </MenuItem>

                      {canSeePaymentsTab && (
                        <MenuItem
                          onClick={() => navigate("/lk/payments", { replace: true })}
                          sx={{
                            borderRadius: "10px", mt: 0.5, color: C.grayLight,
                            '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                          }}
                        >
                          <CreditCardRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                          Платежи
                        </MenuItem>
                      )}

                      {location.pathname != "/lk" && (
                        account?.my_permissions === '__all__' ||
                        (Array.isArray(account?.my_permissions) &&
                          account.my_permissions.includes('view_application'))
                      ) && (
                        <MenuItem
                          onClick={() => navigate("/lk", { replace: true })}
                          sx={{
                            borderRadius: "10px", mt: 0.5, color: C.grayLight,
                            '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                          }}
                        >
                          <BallotRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                          Мои заявки
                        </MenuItem>
                      )}
                      {canSeeAccessTab && !location.pathname.includes("/lk/access") && (
                        <MenuItem
                          onClick={() => navigate("/lk/access", { replace: true })}
                          sx={{
                            borderRadius: "10px", mt: 0.5, color: C.grayLight,
                            '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                          }}
                        >
                          <GroupRoundedIcon sx={{ fontSize: 20, color: C.primaryLight }} />
                          Сотрудники
                        </MenuItem>
                      )}

                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />

                      <MenuItem
                        onClick={logout}
                        sx={{
                          borderRadius: "10px", color: '#ef4444',
                          '&:hover': { background: 'rgba(239,68,68,0.1)' },
                        }}
                      >
                        <LogoutRoundedIcon sx={{ fontSize: 20 }} />
                        Выйти
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                ) : (
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="solid"
                      onClick={() => setModalAuth(true)}
                      sx={{
                        fontWeight: 700, borderRadius: "100px",
                        background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
                        boxShadow: "0 3px 12px rgba(99,102,241,0.3)",
                        px: 3,
                      }}
                    >
                      Войти в ЛК
                    </Button>
                  </motion.div>
                )}

                {/* ── Burger (мобильный) ── */}
                <IconButton
                  onClick={() => setDrawerOpen(o => !o)}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: C.white, borderRadius: '10px', transition: '.3s',
                    '&:hover': { background: 'rgba(255,255,255,0.14)', color: C.white },
                  }}
                >
                  {drawerOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* ── Лента сторис: уезжает при скролле, сворачивается в стопку ── */}
      {isLK && hasStories && (
        <Box
          ref={storyRowRef}
          sx={{
            bgcolor: 'background.body',
            borderBottom: '1px solid', borderColor: 'background.level1',
          }}
        >
          <StoriesRow items={storyItems} onOpen={openStoryAt} />
        </Box>
      )}
      
      {/* ── Mobile Drawer ─────────────────────────────────────── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="right"
        sx={{
          '--Drawer-horizontalSize': 'min(360px, 92vw)',
          '& .MuiDrawer-content': {
            background: C.darkLight, border: 'none',
          },
        }}
      >
        <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 1, height: '100%', overflowY: 'auto' }}>

          {/* Drawer header */}
          <Stack sx={{ px: 3, mb: 1 }} direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box component="img" src="/logo.png" sx={{ width: 32, height: 32 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.white }}>PLANIUM</Typography>
            </Stack>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: C.white, borderRadius: '10px', transition: '.3s',
                '&:hover': { background: 'rgba(255,255,255,0.12)', color: C.white },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          {/* Nav items */}
          <Stack direction="column" sx={{ overflowY: 'auto', px: 2 }}>
            <List sx={{ '--List-gap': '4px', '--ListItem-paddingY': '0px' }}>
              {mainNavItems.map((item) => (
                <ListItem key={item.href}>
                  <ListItemButton
                    component="a"
                    href={item.href}
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      gap: 1.5, borderRadius: '14px', py: 1.5, px: 1.75,
                      color: C.grayLight, fontSize: 15, fontWeight: 500,
                      transition: '.3s',
                      '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'rgba(99,102,241,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, color: C.primaryLight,
                      }}
                    >
                      {item.icon}
                    </Box>
                    {item.label}
                  </ListItemButton>
                </ListItem>
              ))}

              {/* Профиль — если авторизован */}
              {account && (
                <ListItem>
                  <ListItemButton
                    onClick={() => { navigate("/lk/profile"); setDrawerOpen(false); }}
                    sx={{
                      gap: 1.5, borderRadius: '14px', py: 1.5, px: 1.75,
                      color: C.grayLight, fontSize: 15, fontWeight: 500,
                      '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                    }}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px',
                      background: 'rgba(99,102,241,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, color: C.primaryLight }}
                    >
                      <AccountCircleRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} />
                    </Box>
                    Профиль
                  </ListItemButton>
                </ListItem>
              )}

              {/* Документы — если авторизован */}
              {account && (
                <ListItem>
                  <ListItemButton
                    onClick={() => { navigate("/lk/documents"); setDrawerOpen(false); }}
                    sx={{
                      gap: 1.5, borderRadius: '14px', py: 1.5, px: 1.75,
                      color: C.grayLight, fontSize: 15, fontWeight: 500,
                      '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                    }}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px',
                      background: 'rgba(99,102,241,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, color: C.primaryLight }}
                    >
                      <DescriptionRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} />
                    </Box>
                    Документы
                  </ListItemButton>
                </ListItem>
              )}
              
              {/* Мои заявки — если авторизован */}
              {account && (
                <ListItem>
                  <ListItemButton
                    onClick={() => { navigate("/lk"); setDrawerOpen(false); }}
                    sx={{
                      gap: 1.5, borderRadius: '14px', py: 1.5, px: 1.75,
                      color: C.grayLight, fontSize: 15, fontWeight: 500,
                      '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'rgba(99,102,241,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, color: C.primaryLight,
                      }}
                    >
                      <BallotRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} />
                    </Box>
                    Мои заявки
                  </ListItemButton>
                </ListItem>
              )}

              {/* Платежи — если есть право */}
              {account && canSeePaymentsTab && (
                <ListItem>
                  <ListItemButton
                    onClick={() => { navigate("/lk/payments"); setDrawerOpen(false); }}
                    sx={{
                      gap: 1.5, borderRadius: '14px', py: 1.5, px: 1.75,
                      color: C.grayLight, fontSize: 15, fontWeight: 500,
                      '&:hover': { background: 'rgba(99,102,241,0.1)', color: C.white },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'rgba(99,102,241,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, color: C.primaryLight,
                      }}
                    >
                      <CreditCardRoundedIcon sx={{ fontSize: 18, color: C.primaryLight }} />
                    </Box>
                    Платежи
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Stack>

          {/* Phone block */}
          <Box
            sx={{
              mx: 2, mb: 1, mt: 'auto', p: 2,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '14px',
            }}
          >
            <Typography sx={{ fontSize: 11, color: C.gray, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
              Телефон
            </Typography>
            <Box
              component="a"
              href="tel:+79011326059"
              sx={{
                display: 'block', fontSize: 18, fontWeight: 800,
                color: C.white, textDecoration: 'none', mb: 0.5,
              }}
            >
              +7 (901) 132-60-59
            </Box>
            <Typography sx={{ fontSize: 12, color: C.gray }}>
              Пн-Вс 9:00–21:00
            </Typography>
          </Box>

          {/* CTA — Бот */}
          <Box
            component="a"
            href="https://max.ru/id590852001703_bot"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setDrawerOpen(false)}
            sx={{
              mx: 2, display: 'flex', alignItems: 'center',
              color: C.white, justifyContent: 'center', gap: 1,
              background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              fontWeight: 700, p: 1.75, borderRadius: '14px',
              textDecoration: 'none', fontSize: 15,
              boxShadow: '0 3px 12px rgba(99,102,241,0.3)',
              '&:hover': { opacity: 0.9 },
            }}
          >
            <SmartToyRoundedIcon sx={{ fontSize: 18, color: C.white }} />
            {/* Написать боту → */}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}